import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageMeta from '@/components/seo/PageMeta';

const FAQ: React.FC = () => {
  // Remove the navigation-related code since we're standardizing as a regular page
  return (
    <div className="flex flex-col min-h-screen bg-gray-950">
      <PageMeta
        title="Frequently Asked Questions | skore.info"
        description="Get quick answers about how skore.info keeps sports spoiler-free, how game filters work, what sports the app supports, and how to watch without score spoilers."
        canonicalPath="/faq"
        keywords={['FAQ', 'skore.info', 'spoiler-free sports', 'sports filters']}
      />
      <Header />
      
      <main className="container mx-auto px-4 py-8 flex-grow">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">Frequently Asked Questions</h1>
        
        <div className="max-w-4xl mx-auto bg-gray-900/80 p-8 rounded-xl border border-gray-700">
          <div className="prose prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3 text-white">What is skore?</h2>
              <p className="text-gray-300">
                skore is a web application designed to help sports fans explore and watch past games without spoilers. 
                We show you exciting games worth watching based on non-revealing metrics, without showing the final score or outcome.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3 text-white">How does skore filter games?</h2>
              <p className="text-gray-300">
                Our filtering system works differently for each sport:
              </p>
              <ul className="list-disc pl-6 mb-3 text-gray-300">
                <li>NBA: completed games with point difference at or below your selected threshold (default: 10)</li>
                <li>Soccer: completed matches with total goals at or above your selected threshold (default: 1)</li>
                <li>NFL: completed games with point difference at or below your selected threshold (default: 14)</li>
                <li>MLB: completed games with run difference at or below your selected threshold (default: 6)</li>
                <li>NHL: completed games with total goals at or above your selected threshold (default: 1)</li>
              </ul>
              <p className="text-gray-300">
                You can adjust these filters to find games that match your definition of "exciting."
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3 text-white">Do I need to create an account?</h2>
              <p className="text-gray-300">
                No! skore is designed to be used without any login or account creation. Your preferences are saved locally in your browser.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3 text-white">How often is the game data updated?</h2>
              <p className="text-gray-300">
                Game data is updated regularly when new completed games are available. Some sports may show no games during offseasons or gaps in the schedule.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3 text-white">How can I watch the games?</h2>
              <p className="text-gray-300">
                skore doesn't host any video content. We provide information about exciting games, and you can watch them through your preferred sports streaming service or provider that offers replays.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3 text-white">What sports are currently supported?</h2>
              <p className="text-gray-300">
                We support NBA, Soccer (multiple leagues), NFL, MLB, and NHL pages. The current public feed regularly shows NBA, soccer, MLB, and NHL games. NFL availability depends on the current data feed, so the NFL page may temporarily have no games.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3 text-white">Does skore collect my personal data?</h2>
              <p className="text-gray-300">
                We collect minimal data - only what's necessary to provide our service. We use cookies and local storage to remember your preferences. For more details, please check our <a href="/privacy" className="text-indigo-400 hover:text-indigo-300 underline">Privacy Policy</a>.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3 text-white">How can I customize the filtering?</h2>
              <p className="text-gray-300">
                Each sport page has filter options that let you adjust the thresholds for what makes a game "exciting" according to your preferences. These filters are saved for your next visit.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3 text-white">Is skore free to use?</h2>
              <p className="text-gray-300">
                Yes, skore is completely free to use!
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-3 text-white">How can I contact support?</h2>
              <p className="text-gray-300">
                If you have any questions, feedback, or need support, please email us at support@skore.info.
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default FAQ;
